/**
 * CellPopover - the sign-off popover / bottom sheet for one cell (or the row
 * "⋯" N/A entry): info (who/when/note + Add-Edit note, Mark N/A, Untick,
 * History), note, na and history modes.
 */
import { useRef, useState } from 'react';
import {
  BTN,
  BTN_GHOST,
  BTN_PRIMARY,
  GREEN,
  INPUT,
  LABEL,
  LINKISH,
  POPOVER_BOX,
  SHEET_BACKDROP,
  SHEET_BOX,
  SHEET_HANDLE,
  fullStamp,
  useDismiss,
  useIsNarrow,
} from './svmModel';

// Popover over a sign-off cell (or the row "⋯" N/A entry). Fixed-position,
// closes on outside click / Escape. Modes: info | note | na | history.
// Narrow (phone): renders as a full-width bottom sheet with a dim backdrop
// instead of the floating popover - same content and actions.
export default function CellPopover({ pop, check, onClose, doSave, doRemove, doHistory }) {
  const narrow = useIsNarrow();
  const [mode, setMode] = useState(pop.initialMode || 'info');
  const [note, setNote] = useState(check && check.note ? check.note : '');
  const [col, setCol] = useState(pop.col || (pop.colChoices && pop.colChoices[0]) || 'CELL');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [history, setHistory] = useState(null); // null = not fetched
  const [histErr, setHistErr] = useState(null);
  const ref = useRef(null);

  useDismiss(ref, onClose);

  const openHistory = async () => {
    setMode('history');
    setHistErr(null);
    if (history) return;
    try {
      const d = await doHistory(pop.zone, pop.item.no);
      setHistory(d.events || []);
    } catch (e) {
      setHistErr(e.message || 'Failed to load history');
    }
  };

  const run = async (fn) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      onClose();
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  // Desktop position: below the cell, clamped to viewport; flip above if near
  // bottom. Narrow: bottom sheet, position independent of the cell rect.
  const W = 300;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const left = Math.max(8, Math.min(pop.rect.left - 110, vw - W - 12));
  const below = pop.rect.bottom + 8;
  const top = below + 280 > vh ? Math.max(8, pop.rect.top - 288) : below;

  const naEntry = !pop.col; // opened from the row "⋯" menu
  const canMarkNa = check && check.status === 'done';

  // Comfortable touch padding on narrow; desktop keeps the compact buttons.
  const actPad = narrow ? '10px 14px' : '6px 10px';
  const savePad = narrow ? '10px 16px' : '6px 12px';
  const boxStyle = narrow ? SHEET_BOX : { ...POPOVER_BOX, left, top, width: W };

  return (
    <>
      {narrow && <div style={SHEET_BACKDROP} onClick={onClose} />}
      <div ref={ref} style={boxStyle}>
        {narrow && <div style={SHEET_HANDLE} />}
        <div style={{ fontSize: 10.5, color: '#999', marginBottom: 8, lineHeight: 1.4 }}>
          <b style={{ color: '#555' }}>#{pop.item.no}</b> · {pop.item.text.length > 130 ? `${pop.item.text.slice(0, 130)}…` : pop.item.text}
          {pop.col ? <span style={{ marginLeft: 6, fontWeight: 800, color: GREEN }}>{pop.col}</span> : null}
        </div>

        {err && <div style={{ fontSize: 11, color: '#B71C1C', fontWeight: 600, marginBottom: 8 }}>{err}</div>}

        {mode === 'info' &&
          (check ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 800, color: check.status === 'na' ? '#8D6E00' : GREEN, marginBottom: 2 }}>
                {check.status === 'na' ? 'Marked N/A' : 'Done'} · {check.checkedBy || 'Unknown'}
              </div>
              <div style={{ fontSize: 11, color: '#888', marginBottom: check.note ? 6 : 10 }}>{fullStamp(check.checkedAt)}</div>
              {check.note && (
                <div
                  style={{
                    fontSize: 11.5,
                    color: '#444',
                    background: '#FAFAFA',
                    border: '1px solid #EEE',
                    borderRadius: 8,
                    padding: '8px 10px',
                    marginBottom: 10,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {check.note}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  style={{ ...BTN_GHOST, fontSize: 11, padding: actPad }}
                  onClick={() => {
                    setNote(check.note || '');
                    setMode('note');
                  }}
                >
                  {check.note ? 'Edit note' : 'Add note'}
                </button>
                {canMarkNa && (
                  <button
                    type="button"
                    style={{ ...BTN_GHOST, fontSize: 11, padding: actPad, color: '#8D6E00' }}
                    onClick={() => {
                      setNote('');
                      setMode('na');
                    }}
                  >
                    Mark N/A
                  </button>
                )}
                <button
                  type="button"
                  style={{ ...BTN_GHOST, fontSize: 11, padding: actPad, color: '#B71C1C' }}
                  disabled={busy}
                  onClick={() => run(() => doRemove(pop.zone, pop.item.no, pop.col))}
                >
                  {busy ? 'Working…' : 'Untick'}
                </button>
                <button type="button" style={{ ...BTN_GHOST, fontSize: 11, padding: actPad }} onClick={openHistory}>
                  History
                </button>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 11.5, color: '#999' }}>This sign-off was just removed by someone else.</div>
          ))}

        {mode === 'note' && check && (
          <>
            <label style={LABEL}>Note (optional)</label>
            <textarea
              style={{ ...INPUT, minHeight: 70, resize: 'vertical', fontSize: 11.5 }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add context for this sign-off…"
              autoFocus
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button
                type="button"
                style={{ ...BTN_PRIMARY, fontSize: 11, padding: savePad, opacity: busy ? 0.6 : 1 }}
                disabled={busy}
                onClick={() => run(() => doSave(pop.zone, pop.item.no, pop.col, check.status, note.trim()))}
              >
                {busy ? 'Saving…' : 'Save note'}
              </button>
              <button type="button" style={{ ...BTN_GHOST, fontSize: 11, padding: savePad }} onClick={() => setMode('info')}>
                Cancel
              </button>
            </div>
          </>
        )}

        {mode === 'na' && (
          <>
            {naEntry && pop.colChoices && pop.colChoices.length > 1 && (
              <div style={{ marginBottom: 8 }}>
                <label style={LABEL}>Robot</label>
                <select style={{ ...INPUT, fontSize: 11.5 }} value={col} onChange={(e) => setCol(e.target.value)}>
                  {pop.colChoices.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {naEntry && pop.colChoices && pop.colChoices.length === 1 && (
              <div style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Marks the whole-cell sign-off N/A.</div>
            )}
            <label style={LABEL}>Reason (required for N/A)</label>
            <textarea
              style={{ ...INPUT, minHeight: 70, resize: 'vertical', fontSize: 11.5 }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why doesn't this item apply? (needs PM approval)"
              autoFocus
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button
                type="button"
                style={{
                  ...BTN,
                  background: '#8D6E00',
                  color: '#fff',
                  fontSize: 11,
                  padding: savePad,
                  opacity: busy || !note.trim() ? 0.5 : 1,
                }}
                disabled={busy || !note.trim()}
                onClick={() => run(() => doSave(pop.zone, pop.item.no, pop.col || col, 'na', note.trim()))}
              >
                {busy ? 'Saving…' : 'Mark N/A'}
              </button>
              <button
                type="button"
                style={{ ...BTN_GHOST, fontSize: 11, padding: savePad }}
                onClick={() => (naEntry ? onClose() : setMode('info'))}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {mode === 'history' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 11.5, fontWeight: 800, color: '#0D0D0D' }}>History</span>
              <button type="button" style={{ ...LINKISH, fontSize: 11, color: GREEN, fontWeight: 700 }} onClick={() => setMode('info')}>
                ← Back
              </button>
            </div>
            {histErr && <div style={{ fontSize: 11, color: '#B71C1C' }}>{histErr}</div>}
            {!histErr && history === null && <div style={{ fontSize: 11, color: '#999' }}>Loading…</div>}
            {history && history.length === 0 && <div style={{ fontSize: 11, color: '#999' }}>No events recorded yet.</div>}
            {history &&
              history.map((ev, i) => (
                <div key={i} style={{ borderBottom: '1px solid #F2F2F2', padding: '6px 0', fontSize: 11 }}>
                  <span
                    style={{
                      fontWeight: 800,
                      color: ev.action === 'uncheck' ? '#B71C1C' : ev.action === 'na' ? '#8D6E00' : GREEN,
                      textTransform: 'uppercase',
                      fontSize: 9.5,
                      letterSpacing: 0.5,
                    }}
                  >
                    {ev.action}
                  </span>
                  <span style={{ color: '#555', marginLeft: 6 }}>{ev.col}</span>
                  <span style={{ color: '#333', marginLeft: 6, fontWeight: 600 }}>{ev.actorName || 'Unknown'}</span>
                  <span style={{ color: '#999', marginLeft: 6 }}>{fullStamp(ev.at)}</span>
                  {ev.note && <div style={{ color: '#777', marginTop: 2, whiteSpace: 'pre-wrap' }}>{ev.note}</div>}
                </div>
              ))}
          </>
        )}
      </div>
    </>
  );
}
